// The pure half of the player strip (05c) — main.js initPlayerSearch's search rules and
// the linked-players decision, split out so they're unit-testable without a DOM.

import type { PlayerId } from "../data/players.js";

/** One `<option>` of the hidden `.player-select`, as the search reads it. */
export interface PlayerOption {
  value: string;
  text: string;
  /** `data-search` (short name, full name, nickname, club, league, country). */
  search: string | undefined;
}

/** One line of the results list, in display order. */
export type SearchLine =
  | { kind: "hint"; text: string }
  /** A club's name over its players, in the live search (C2: Ben's "a club finds its players"). */
  | { kind: "heading"; text: string; league?: string; countryCode?: string | null }
  /** "Teams" / "Players" over each group when a search lists teams (teams and matches, 2026-10-30). */
  | { kind: "label"; text: string }
  /** A player, or with `team` a team (its league and the league's flag). */
  | {
      kind: "row";
      value: string;
      text: string;
      selected: boolean;
      nationality?: string | null;
      clubCountry?: string | null;
      team?: { league: string; countryCode: string | null };
    };

/** main.js's cap — the real list is 1,000+ names, so only a manageable slice is drawn. */
export const SEARCH_ROW_CAP = 40;

/**
 * main.js buildRows: an empty query shows a "type to search" hint, no match shows
 * "No matches", else up to 40 matching rows (the current player marked) and a
 * "+ N more" hint. `selected` is fixed when the list is built, as in main.js — a pick
 * doesn't re-mark the rows still sitting in the closed list.
 */
export function searchPlayerOptions(options: readonly PlayerOption[], query: string, currentValue: string): SearchLine[] {
  const q = query.trim().toLowerCase();
  if (!q) return [{ kind: "hint", text: `Type to search ${options.length.toLocaleString()} players…` }];
  const hits = options.filter((o) => (o.search || o.text).toLowerCase().includes(q));
  if (!hits.length) return [{ kind: "hint", text: "No matches" }];
  const lines: SearchLine[] = hits
    .slice(0, SEARCH_ROW_CAP)
    .map((o) => ({ kind: "row", value: o.value, text: o.text, selected: o.value === currentValue }));
  if (hits.length > SEARCH_ROW_CAP) {
    lines.push({ kind: "hint", text: `+ ${hits.length - SEARCH_ROW_CAP} more — keep typing to narrow` });
  }
  return lines;
}

/** Where a live search box is (ids-3): too short to ask, asking, failed, or answered. */
export type LiveSearchState =
  | { kind: "short" }
  | { kind: "searching" }
  | { kind: "failed" }
  | { kind: "done"; rows: LiveSearchRow[] };

/** A player row, or a club heading over the rows that follow it. */
/** A player row (with its flags, ISO alpha-3, when known), or a club heading. */
export type LiveSearchRow =
  | { value: string; text: string; nationality?: string | null; clubCountry?: string | null; team?: { league: string; countryCode: string | null } }
  | { heading: string; league?: string; countryCode?: string | null }
  | { label: string };

/** The live list shows every match (C2), but a 3-letter query can match 10,000+: drawing that
 *  many rows on every key would lag, and nobody scrolls that far. Past this, a hint says keep typing. */
export const LIVE_ROW_CAP = 1000;

/**
 * The live search's lines (`?live=1`): the same list look as the snapshot's, with the
 * real search's states as hints. `currentValue` marks the module's player, as above.
 */
export function liveSearchLines(state: LiveSearchState, currentValue: string): SearchLine[] {
  switch (state.kind) {
    case "short":
      return [{ kind: "hint", text: "Type 3+ letters to search all players…" }];
    case "searching":
      return [{ kind: "hint", text: "Searching…" }];
    case "failed":
      return [{ kind: "hint", text: "Search failed — keep typing to try again" }];
    case "done": {
      if (!state.rows.length) return [{ kind: "hint", text: "No matches" }];
      const lines: SearchLine[] = [];
      let shown = 0;
      let more = 0;
      for (const r of state.rows) {
        if ("label" in r) {
          if (shown < LIVE_ROW_CAP) lines.push({ kind: "label", text: r.label });
        } else if ("heading" in r) {
          if (shown < LIVE_ROW_CAP) lines.push({ kind: "heading", text: r.heading, league: r.league, countryCode: r.countryCode });
        } else if (shown < LIVE_ROW_CAP) {
          lines.push({ kind: "row", value: r.value, text: r.text, selected: r.value === currentValue, nationality: r.nationality, clubCountry: r.clubCountry, team: r.team });
          shown += 1;
        } else more += 1;
      }
      // A heading with no rows under it (the cap fell right after it) would look broken.
      if (lines.at(-1)?.kind === "heading") lines.pop();
      if (more > 0) lines.push({ kind: "hint", text: `+ ${more.toLocaleString()} more — keep typing to narrow` });
      return lines;
    }
  }
}

/** What a linked pick does to one other selected module. */
export type LinkDecision =
  /** No player picker at all (League Table, Notes…) — left alone, no flash. */
  | "none"
  /** Can't take this player (a comparison, or Goalkeeping and not a goalkeeper) — flashes. */
  | "skip"
  /** Already on this player. */
  | "same"
  | "take";

/** A module as the linked pick sees it. */
export interface LinkTarget {
  hasPicker: boolean;
  /** Has `_mergedPlayers` — a comparison has no single player to replace. */
  merged: boolean;
  /** Its picker lists this player. */
  offersPlayer: boolean;
  /** Its current player's id (null: an empty picker). */
  currentValue: PlayerId | null;
}

/** main.js applyLinkedPlayer's per-module decision (with canTakePlayer). */
export function linkDecision(target: LinkTarget, playerId: PlayerId): LinkDecision {
  if (!target.hasPicker) return "none";
  if (target.merged || !target.offersPlayer) return "skip";
  if (target.currentValue === playerId) return "same";
  return "take";
}

/** main.js refreshLinkHints: "Changes N" on each selected player module once 2+ are
 *  selected (a comparison doesn't count), else no hint. */
export function linkHintText(linkableCount: number): string | null {
  return linkableCount < 2 ? null : `Changes ${linkableCount}`;
}
